/**
 * Languages an admin can translate widget content into (shortcut cards, prompt
 * shortcuts, welcome-screen text).
 *
 * This is deliberately separate from the admin panel's own UI locales in
 * `src/i18n/locales/` — the admin interface ships in en/de/es, but the content
 * an admin authors can target any audience their widget serves.
 *
 * Display names are stored here rather than as i18n keys: localizing 30 language
 * names across every locale file is pure churn, and admins recognize a language
 * by its native name regardless of the UI language they are using.
 */

export interface ContentLanguageOption {
  /** Normalized (lowercase, hyphenated) code — this is what the API stores. */
  code: string;
  /** Name in the language itself, shown on the tab and in the picker. */
  nativeName: string;
  /** English name, used to match a search term the admin might type. */
  englishName: string;
}

/**
 * Must stay in sync with the backend's accepted tag shape
 * (`ContentLanguage.Normalize`: 2-3 letters, optional 2-letter or 3-digit region).
 */
export const CONTENT_LANGUAGES: ContentLanguageOption[] = [
  { code: "en", nativeName: "English", englishName: "English" },
  { code: "de", nativeName: "Deutsch", englishName: "German" },
  { code: "es", nativeName: "Español", englishName: "Spanish" },
  { code: "fr", nativeName: "Français", englishName: "French" },
  { code: "it", nativeName: "Italiano", englishName: "Italian" },
  { code: "pt", nativeName: "Português", englishName: "Portuguese" },
  {
    code: "pt-br",
    nativeName: "Português (Brasil)",
    englishName: "Portuguese (Brazil)",
  },
  { code: "nl", nativeName: "Nederlands", englishName: "Dutch" },
  { code: "pl", nativeName: "Polski", englishName: "Polish" },
  { code: "sv", nativeName: "Svenska", englishName: "Swedish" },
  { code: "da", nativeName: "Dansk", englishName: "Danish" },
  { code: "no", nativeName: "Norsk", englishName: "Norwegian" },
  { code: "fi", nativeName: "Suomi", englishName: "Finnish" },
  { code: "cs", nativeName: "Čeština", englishName: "Czech" },
  { code: "sk", nativeName: "Slovenčina", englishName: "Slovak" },
  { code: "hu", nativeName: "Magyar", englishName: "Hungarian" },
  { code: "ro", nativeName: "Română", englishName: "Romanian" },
  { code: "bg", nativeName: "Български", englishName: "Bulgarian" },
  { code: "el", nativeName: "Ελληνικά", englishName: "Greek" },
  { code: "tr", nativeName: "Türkçe", englishName: "Turkish" },
  { code: "ru", nativeName: "Русский", englishName: "Russian" },
  { code: "uk", nativeName: "Українська", englishName: "Ukrainian" },
  { code: "ar", nativeName: "العربية", englishName: "Arabic" },
  { code: "he", nativeName: "עברית", englishName: "Hebrew" },
  { code: "hi", nativeName: "हिन्दी", englishName: "Hindi" },
  { code: "id", nativeName: "Bahasa Indonesia", englishName: "Indonesian" },
  { code: "th", nativeName: "ไทย", englishName: "Thai" },
  { code: "vi", nativeName: "Tiếng Việt", englishName: "Vietnamese" },
  { code: "ja", nativeName: "日本語", englishName: "Japanese" },
  { code: "ko", nativeName: "한국어", englishName: "Korean" },
  {
    code: "zh",
    nativeName: "中文 (简体)",
    englishName: "Chinese (Simplified)",
  },
  {
    code: "zh-tw",
    nativeName: "中文 (繁體)",
    englishName: "Chinese (Traditional)",
  },
];

/** Mirrors `ContentLanguage.MaxLanguagesPerItem` on the backend. */
export const MAX_CONTENT_LANGUAGES = 20;

/**
 * Lowercase and hyphenate a language tag the way the backend does.
 * Returns null when the tag isn't a shape the API would accept.
 */
export function normalizeContentLanguage(raw: string): string | null {
  const normalized = raw.trim().toLowerCase().replace(/_/g, "-");
  return /^[a-z]{2,3}(-([a-z]{2}|[0-9]{3}))?$/.test(normalized)
    ? normalized
    : null;
}

/** Look up a curated language by code. Returns undefined for codes not in the list. */
export function findContentLanguage(
  code: string,
): ContentLanguageOption | undefined {
  const normalized = normalizeContentLanguage(code);
  return normalized
    ? CONTENT_LANGUAGES.find((l) => l.code === normalized)
    : undefined;
}

/**
 * Human label for a language code. Falls back to the raw code so a translation
 * stored for a language outside the curated list still renders a usable tab.
 */
export function contentLanguageLabel(code: string): string {
  return findContentLanguage(code)?.nativeName ?? code.toUpperCase();
}
